import React, { useRef, useEffect } from 'react';

interface ModalAcessivelProps {
  isOpen: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
}

const ModalAcessivel: React.FC<ModalAcessivelProps> = ({ isOpen, onRequestClose, children }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      lastFocusedElementRef.current = document.activeElement as HTMLElement;
      const focusableElementsString =
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
      const focusableElements = modalRef.current?.querySelectorAll(focusableElementsString);
      const firstFocusableElement = focusableElements ? (focusableElements[0] as HTMLElement) : null;
      const lastFocusableElement = focusableElements ? (focusableElements[focusableElements.length - 1] as HTMLElement) : null;

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }

      const trapFocus = (event: KeyboardEvent) => {
        if (!focusableElements) return;

        const firstFocusableElement = focusableElements[0] as HTMLElement;
        const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.key === 'Tab') {
          if (event.shiftKey) {
            // shift + tab
            if (document.activeElement === firstFocusableElement) {
              lastFocusableElement.focus();
              event.preventDefault();
            }
          } else {
            // tab
            if (document.activeElement === lastFocusableElement) {
              firstFocusableElement.focus();
              event.preventDefault();
            }
          }
        }

        if (event.key === 'Escape') {
          handleCloseModal();
        }

        if (event.ctrlKey && event.key === 'Home') {
          firstFocusableElement.focus();
          event.preventDefault();
        }

        if (event.ctrlKey && event.key === 'End') {
          lastFocusableElement.focus();
          event.preventDefault();
        }
      };

      document.addEventListener('keydown', trapFocus);

      return () => {
        document.removeEventListener('keydown', trapFocus);
      };
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    onRequestClose();
    if (lastFocusedElementRef.current) {
      lastFocusedElementRef.current.focus();
    }
  };

  return (
    <div ref={modalRef} style={{ display: isOpen ? 'flex' : 'none' }} role="dialog" aria-modal="true" className='w-full justify-center'>
      {children}
    </div>
  );
};

export default ModalAcessivel;
